/**
 * The quantity rule for a standalone consumable line: whole bags or bottles, from 1 to 9,999.
 *
 * The ceiling is what the line can actually be stored as — quote_product.quantity is
 * decimal(8,4), so 9,999,999 went in as 9,999.9999 and every total was worked out from that.
 * The API enforces the same range (quoteProductValidator), so a value that gets past a screen
 * is still refused rather than silently altered.
 */
export const CONSUMABLE_MIN_QUANTITY = 1;
export const CONSUMABLE_MAX_QUANTITY = 9999;

/** Digits only. Keypress filtering cannot see a paste, a drop or an autofill; this can. */
export const consumableQuantityDigits = (value: unknown): string => String(value ?? '').replace(/[^0-9]/g, '');

/**
 * Keep an input box showing exactly its cleaned value. Needed because Angular only re-renders
 * a binding whose value changed since the last check: a box cleared from 1 (model 1 → 1) or a
 * paste of "@#$" into an empty box (null → null) looks unchanged to it, so the raw text stayed
 * on screen over a different saved value. Returns the digits.
 */
export const syncConsumableQuantityInput = (event: Event | null | undefined): string => {
  const input = event?.target as HTMLInputElement | null;
  const digits = consumableQuantityDigits(input?.value);
  if (input && input.value !== digits) input.value = digits;
  return digits;
};

/** Why a consumable quantity cannot be saved, or null when it can. */
export const consumableQuantityError = (quantity: unknown): string | null => {
  if (quantity === null || quantity === undefined || String(quantity).trim() === '') {
    return 'Please enter a quantity.';
  }
  const n = Number(quantity);
  if (!Number.isInteger(n) || n < CONSUMABLE_MIN_QUANTITY) {
    return `Quantity must be at least ${CONSUMABLE_MIN_QUANTITY}.`;
  }
  if (n > CONSUMABLE_MAX_QUANTITY) {
    return `Quantity cannot be more than ${CONSUMABLE_MAX_QUANTITY.toLocaleString('en-AU')}.`;
  }
  return null;
};

/**
 * Whether the minus stepper has nothing left to do: the line is at the minimum (or the box holds
 * 0 or nothing). Removing the line is what the delete button is for, so the minus is switched off
 * here rather than left to send a quantity the API refuses, or to raise an empty box to 1.
 */
export const isConsumableQuantityAtMinimum = (quantity: unknown): boolean =>
  (Math.floor(Number(quantity)) || 0) <= CONSUMABLE_MIN_QUANTITY;

/** A stepper press: one bag either way, never outside the range. */
export const stepConsumableQuantity = (current: unknown, direction: 'increment' | 'decrement'): number => {
  const base = Math.floor(Number(current)) || 0;
  const next = direction === 'increment' ? base + 1 : base - 1;
  return Math.min(CONSUMABLE_MAX_QUANTITY, Math.max(CONSUMABLE_MIN_QUANTITY, next));
};
